/**
 * CountdownDisplay — big centered "3 → 2 → 1 → GO!" text overlay.
 *
 * Pure presentation: the game owns the timer (most games already track a
 * `phase` + `phaseAt` + `phaseLen`) and calls `update(remainingMs)` every
 * frame. The display only re-renders + plays the scale/alpha tween when
 * the visible label changes, so calling it every frame is cheap.
 *
 * Extracted from horse-dash, puff-up, tap-tug, fruit-grab, simon-says,
 * shape-rush, block-rain — all of which had a near-identical implementation.
 *
 * Usage:
 *   import { CountdownDisplay } from '@platform/sdk';
 *
 *   start() {
 *     this._countdown = new CountdownDisplay({ scene: this });
 *   }
 *
 *   update() {
 *     if (this.state.phase === 'countdown') {
 *       const remainingMs = this.state.phaseLen - this.state.phaseAt;
 *       const label = this._countdown.update(remainingMs);
 *       if (label === 'GO!') this.sdk.audio.playSfx('go');
 *       else if (label) this.sdk.audio.playSfx('tick');
 *     } else {
 *       this._countdown.hide();
 *     }
 *   }
 */
export class CountdownDisplay {
  /**
   * @param {object} opts
   * @param {Phaser.Scene} opts.scene
   * @param {number} [opts.x]              defaults to scene center
   * @param {number} [opts.y]              defaults to ~42% of scene height
   * @param {string} [opts.fontSize='160px']
   * @param {string} [opts.color='#ffffff']
   * @param {string} [opts.stroke='#dc2626']
   * @param {number} [opts.strokeThickness=12]
   * @param {number} [opts.depth=300]
   * @param {string} [opts.goLabel='GO!']  text shown at 0
   */
  constructor(opts) {
    const scene = opts.scene;
    this.scene = scene;
    this.goLabel = opts.goLabel || 'GO!';

    const x = opts.x != null ? opts.x : scene.scale.width / 2;
    const y = opts.y != null ? opts.y : scene.scale.height * 0.42;

    this.text = scene.add.text(x, y, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: opts.fontSize || '160px',
      fontStyle: 'bold',
      color: opts.color || '#ffffff',
      stroke: opts.stroke || '#dc2626',
      strokeThickness: opts.strokeThickness != null ? opts.strokeThickness : 12
    }).setOrigin(0.5).setDepth(opts.depth != null ? opts.depth : 300);
    this.text.setVisible(false);

    this._lastLabel = '';
  }

  /**
   * Update the countdown for the current frame.
   * @param {number} remainingMs   how many milliseconds remain in the phase
   * @returns {string|null}  the label that was just rendered ('3', '2', '1',
   *   'GO!') *only when it changed this frame*, otherwise null. Use this to
   *   trigger SFX in sync with the visible tick.
   */
  update(remainingMs) {
    const remaining = Math.ceil(Math.max(0, remainingMs) / 1000);
    const label = remaining > 0 ? String(remaining) : this.goLabel;
    let emitted = null;
    if (this._lastLabel !== label) {
      this._lastLabel = label;
      this.text.setText(label);
      this.scene.tweens.killTweensOf(this.text);
      this.text.setScale(1.5);
      this.text.alpha = 0;
      this.scene.tweens.add({
        targets: this.text,
        scale: 1,
        alpha: 1,
        duration: 280,
        ease: 'Cubic.out'
      });
      emitted = label;
    }
    if (!this.text.visible) this.text.setVisible(true);
    return emitted;
  }

  /** Hide the countdown text. Call when leaving the countdown phase. */
  hide() {
    if (this.text && this.text.visible) {
      this.text.setVisible(false);
      this._lastLabel = '';
    }
  }

  /** Tear down the underlying Phaser text. Call on scene shutdown. */
  destroy() {
    if (this.scene && this.text) {
      this.scene.tweens.killTweensOf(this.text);
      this.text.destroy();
    }
    this.text = null;
    this.scene = null;
  }
}
